import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useUserRole } from "@/hooks/useUserRole";
import { useSecurityLogger } from "@/hooks/useSecurityLogger";
import { supabase } from "@/integrations/supabase/client";
import { ContentSkeleton } from "@/components/dashboard/premium/ContentSkeleton";

interface InstructorRouteProps {
  children: React.ReactNode;
}

/**
 * Instructor gate. Renders INSIDE the persistent AppShellLayout — no
 * full-screen spinner, no remount of sidebar/topbar across navigation.
 */
export function InstructorRoute({ children }: InstructorRouteProps) {
  const { isAdmin, isInstructor, isLoading } = useUserRole();
  const navigate = useNavigate();
  const [shouldRender, setShouldRender] = useState(true);
  const redirectTimeout = useRef<NodeJS.Timeout>();
  const { logSecurityEvent } = useSecurityLogger();
  const hasLoggedRef = useRef(false);

  useEffect(() => {
    const logUnauthorizedAttempt = async () => {
      if (!isLoading && !isAdmin && !isInstructor && !hasLoggedRef.current) {
        hasLoggedRef.current = true;
        const { data: { user } } = await supabase.auth.getUser();

        await logSecurityEvent({
          event_type: "unauthorized_access",
          description: `Unauthorized instructor area access attempt by ${user?.email || "unauthenticated user"}`,
          severity: "high",
          user_id: user?.id,
          was_blocked: true,
          details: {
            attempted_route: window.location.pathname,
            user_email: user?.email,
            timestamp: new Date().toISOString(),
          },
        });
      }
    };

    if (!isLoading && !isAdmin && !isInstructor) {
      logUnauthorizedAttempt();
      redirectTimeout.current = setTimeout(() => {
        setShouldRender(false);
        navigate("/dashboard");
      }, 300);
    } else {
      setShouldRender(true);
      if (redirectTimeout.current) {
        clearTimeout(redirectTimeout.current);
      }
    }

    return () => {
      if (redirectTimeout.current) {
        clearTimeout(redirectTimeout.current);
      }
    };
  }, [isAdmin, isInstructor, isLoading, navigate, logSecurityEvent]);

  if (isLoading) {
    return <ContentSkeleton />;
  }

  if ((!isAdmin && !isInstructor) || !shouldRender) {
    return null;
  }

  return <>{children}</>;
}
